import { haversineKm } from "../lib/geo.js";
import "./FlightProgress.css";

function hasCoords(p) {
  return p && Number.isFinite(p.latitude) && Number.isFinite(p.longitude);
}

function airportLabel(airport) {
  return airport?.iata || airport?.icao || "?";
}

function roundKm(km) {
  return Math.round(km).toLocaleString();
}

function etaText(remainingKm, velocity) {
  // velocity is m/s → km/h; minutes to arrival
  const kmh = velocity * 3.6;
  const minutes = Math.round((remainingKm / kmh) * 60);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  if (minutes < 60) return `arriving in ~${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `arriving in ~${h} h ${m} min`;
}

export default function FlightProgress({ aircraft, route }) {
  const origin = route?.origin;
  const destination = route?.destination;

  if (
    !hasCoords(origin) ||
    !hasCoords(destination) ||
    !aircraft ||
    !Number.isFinite(aircraft.latitude) ||
    !Number.isFinite(aircraft.longitude)
  ) {
    return null;
  }

  const total = haversineKm(
    origin.latitude,
    origin.longitude,
    destination.latitude,
    destination.longitude,
  );
  if (!(total > 0)) return null;

  const remainingRaw = haversineKm(
    aircraft.latitude,
    aircraft.longitude,
    destination.latitude,
    destination.longitude,
  );
  const remaining = Math.min(Math.max(remainingRaw, 0), total);
  const flown = total - remaining;
  const percent = Math.round((flown / total) * 100);

  const velocity = aircraft.velocity;
  const eta =
    Number.isFinite(velocity) && velocity > 50
      ? etaText(remaining, velocity)
      : null;

  return (
    <div className="flight-progress">
      <div className="fp-ends">
        <span className="fp-end fp-origin">{airportLabel(origin)}</span>
        <span className="fp-end fp-dest">{airportLabel(destination)}</span>
      </div>
      <div
        className="fp-track"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Journey ${percent}% complete, from ${airportLabel(
          origin,
        )} to ${airportLabel(destination)}`}
      >
        <div className="fp-fill" style={{ width: `${percent}%` }} />
      </div>
      <p className="fp-text">
        ≈ {roundKm(flown)} km flown · {roundKm(remaining)} km to go
        {eta ? <span className="fp-eta"> · {eta}</span> : null}
      </p>
    </div>
  );
}
